import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

import "../../admin/AdminDashboard.css";

const GOOGLE_API_KEY = "AIzaSyA93FPo9uPGHk0g8FFvaZSKokn4n1tLmag";

export default function AdminEditClue() {
  const { id, clueId } = useParams();
  const navigate = useNavigate();

  const [clue, setClue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCaseFile, setUploadingCaseFile] = useState(false);
  const [uploadingSuccessImage, setUploadingSuccessImage] = useState(false);
  const [missingNextLocation, setMissingNextLocation] = useState(false);

  useEffect(() => {
    async function loadClue() {
      try {
        const snap = await getDoc(doc(db, "clues", clueId));

        if (!snap.exists()) {
          alert("Clue not found.");
          navigate("/trailseditor");
          return;
        }

        const d = snap.data();

        if (!("nextlocation" in d)) {
          setMissingNextLocation(true);
        }

        setClue({
          order: d.order ?? 0,
          question: d.question ?? "",
          answer: d.answer ?? "",
          hint: d.hint ?? "",
          helpfultip: d.helpfultip ?? "",
          nextlocation: d.nextlocation ?? "",
          imageURL: d.imageURL ?? "",
          caseFiles: d.caseFiles ?? [],
          successImage: d.successImage ?? "",
          trailId: d.trailId
        });

      } catch (err) {
        alert("Error loading clue.");
      }

      setLoading(false);
    }

    loadClue();
  }, [clueId, navigate]);

  function buildMapEmbed(location) {
    if (!location) return null;
    const trimmed = location.trim();
    if (/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(trimmed)) {
      const [lat, lng] = trimmed.split(",");
      return `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_API_KEY}&center=${lat.trim()},${lng.trim()}&zoom=16`;
    }
    return null;
  }

  async function uploadClueImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);

    try {
      const storageRef = ref(storage, `clues/${clue.trailId}/clue_${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setClue((prev) => ({ ...prev, imageURL: url }));
    } catch (err) {
      alert("Failed to upload image.");
    }

    setUploadingImage(false);
  }

  async function deleteClueImage() {
    if (!clue.imageURL) return;
    if (!window.confirm("Delete this clue image?")) return;

    try {
      const path = decodeURIComponent(
        clue.imageURL.split("/o/")[1].split("?")[0]
      );
      await deleteObject(ref(storage, path));
      setClue((prev) => ({ ...prev, imageURL: "" }));
      alert("Image deleted.");
    } catch (err) {
      alert("Failed to delete image.");
    }
  }

  async function uploadCaseFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCaseFile(true);

    try {
      const storageRef = ref(storage, `clues/${clue.trailId}/case_files/casefile_${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setClue((prev) => ({
        ...prev,
        caseFiles: [...(prev.caseFiles || []), url]
      }));
      alert("Case file uploaded!");
    } catch (err) {
      alert("Failed to upload case file.");
    }

    setUploadingCaseFile(false);
    e.target.value = "";
  }

  async function deleteCaseFile(fileUrl) {
    if (!window.confirm("Delete this case file?")) return;

    try {
      const path = decodeURIComponent(
        fileUrl.split("/o/")[1].split("?")[0]
      );
      await deleteObject(ref(storage, path));
      setClue((prev) => ({
        ...prev,
        caseFiles: prev.caseFiles.filter(url => url !== fileUrl)
      }));
      alert("Case file deleted.");
    } catch (err) {
      alert("Failed to delete case file.");
    }
  }

  async function uploadSuccessImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingSuccessImage(true);

    try {
      const storageRef = ref(storage, `clues/${clue.trailId}/success/success_${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setClue((prev) => ({ ...prev, successImage: url }));
      alert("Success image uploaded!");
    } catch (err) {
      alert("Failed to upload success image.");
    }

    setUploadingSuccessImage(false);
    e.target.value = "";
  }

  async function deleteSuccessImage() {
    if (!clue.successImage) return;
    if (!window.confirm("Delete this success image?")) return;

    try {
      const path = decodeURIComponent(
        clue.successImage.split("/o/")[1].split("?")[0]
      );
      await deleteObject(ref(storage, path));
      setClue((prev) => ({ ...prev, successImage: "" }));
      alert("Success image deleted.");
    } catch (err) {
      alert("Failed to delete success image.");
    }
  }

  // Get the trail ID from either the URL param or the clue data
  const trailId = id || clue?.trailId;

  async function save(e) {
    e.preventDefault();
    setSaving(true);

    try {
      await updateDoc(doc(db, "clues", clueId), { ...clue });
      alert("Clue updated!");
      navigate(`/trailseditor/${trailId}/clues`);
    } catch (err) {
      alert("Save failed.");
    }

    setSaving(false);
  }

  if (loading || !clue) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading clue…</p>
        </div>
      </div>
    );
  }

  const mapEmbed = buildMapEmbed(clue.nextlocation);

  return (
    <div className="dash-page">
      <div className="dash-container">
        <div style={{ marginBottom: "20px" }}>
          <button
            className="dash-btn secondary"
            onClick={() => navigate(`/trailseditor/${trailId}/clues`)}
            style={{ width: "auto" }}
          >
            ← Back to Clues
          </button>
        </div>

        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">Edit Clue</h1>
            <p className="dash-subtitle">Update the clue details below</p>
          </div>
        </header>

        <form className="admin-form-large" onSubmit={save}>

          {/* CLUE IMAGE */}
          <div className="section-box">
            <h2 className="section-title">🖼️ Clue Image (Optional)</h2>

            {clue.imageURL && (
              <div style={{ marginBottom: "20px" }}>
                <img
                  src={clue.imageURL}
                  alt="Clue"
                  className="banner-preview"
                />
                <button
                  type="button"
                  className="dash-btn secondary"
                  onClick={deleteClueImage}
                  style={{ 
                    width: "auto",
                    background: "rgba(239, 68, 68, 0.1)",
                    borderColor: "rgba(239, 68, 68, 0.3)",
                    color: "var(--color-error)"
                  }}
                >
                  🗑 Delete Image
                </button>
              </div>
            )}

            <label className="admin-label">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={uploadClueImage}
              className="admin-input"
            />
            {uploadingImage && (
              <p style={{ color: "var(--color-accent)", marginTop: "8px" }}>
                Uploading image…
              </p>
            )}
          </div>

          {/* CASE FILES */}
          <div className="section-box">
            <h2 className="section-title">📁 Case Files</h2>
            <p style={{ 
              fontSize: "0.85em", 
              color: "var(--color-text-secondary)", 
              marginTop: "-12px", 
              marginBottom: "16px" 
            }}>
              Upload multiple images/documents that players can view as clues
            </p>

            {clue.caseFiles && clue.caseFiles.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: "12px",
                marginBottom: "20px"
              }}>
                {clue.caseFiles.map((fileUrl, index) => (
                  <div key={index} style={{ position: "relative" }}>
                    <img
                      src={fileUrl}
                      alt={`Case file ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid var(--color-border)"
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => deleteCaseFile(fileUrl)}
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        background: "var(--color-error)",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                      title="Delete case file"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="admin-label">Add Case File</label>
            <input
              type="file"
              accept="image/*"
              onChange={uploadCaseFile}
              className="admin-input"
            />
            {uploadingCaseFile && (
              <p style={{ color: "var(--color-accent)", marginTop: "8px" }}>
                Uploading case file…
              </p>
            )}
          </div>

          {/* SUCCESS IMAGE */}
          <div className="section-box">
            <h2 className="section-title">🎉 Success Image</h2>
            <p style={{ 
              fontSize: "0.85em", 
              color: "var(--color-text-secondary)", 
              marginTop: "-12px", 
              marginBottom: "16px" 
            }}>
              Image that displays when the clue is successfully solved
            </p>

            {clue.successImage && (
              <div style={{ marginBottom: "20px" }}>
                <img
                  src={clue.successImage}
                  alt="Success"
                  style={{
                    width: "200px",
                    height: "200px",
                    objectFit: "cover",
                    borderRadius: "12px",
                    border: "3px solid var(--color-success)",
                    display: "block",
                    marginBottom: "12px"
                  }}
                />
                <button
                  type="button"
                  className="dash-btn secondary"
                  onClick={deleteSuccessImage}
                  style={{ 
                    width: "auto",
                    background: "rgba(239, 68, 68, 0.1)",
                    borderColor: "rgba(239, 68, 68, 0.3)",
                    color: "var(--color-error)"
                  }}
                >
                  🗑 Delete Success Image
                </button>
              </div>
            )}

            <label className="admin-label">Upload Success Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={uploadSuccessImage}
              className="admin-input"
            />
            {uploadingSuccessImage && (
              <p style={{ color: "var(--color-accent)", marginTop: "8px" }}>
                Uploading success image…
              </p>
            )}
          </div>

          {/* CLUE ORDER */}
          <div className="section-box">
            <h2 className="section-title">🔢 Clue Order</h2>

            <label className="admin-label">Order Number</label>
            <input
              type="number"
              className="admin-input"
              value={clue.order}
              onChange={(e) => setClue({ ...clue, order: Number(e.target.value) })}
              style={{ maxWidth: "150px" }}
            />
          </div>

          {/* QUESTION & ANSWER */}
          <div className="section-box">
            <h2 className="section-title">❓ Question & Answer</h2>

            <label className="admin-label">Question</label>
            <textarea
              className="admin-input admin-textarea"
              rows="4"
              value={clue.question}
              onChange={(e) => setClue({ ...clue, question: e.target.value })}
              placeholder="Enter the clue question..."
            />

            <label className="admin-label" style={{ marginTop: "16px" }}>Correct Answer</label>
            <input
              className="admin-input"
              value={clue.answer}
              onChange={(e) => setClue({ ...clue, answer: e.target.value })}
              placeholder="Enter the correct answer..."
            />
          </div>

          {/* HINTS */}
          <div className="section-box">
            <h2 className="section-title">💡 Hints & Tips</h2>

            <label className="admin-label">Hint (Optional)</label>
            <textarea
              className="admin-input admin-textarea"
              rows="3"
              value={clue.hint}
              onChange={(e) => setClue({ ...clue, hint: e.target.value })}
              placeholder="A hint to help players who are stuck..."
            />

            <label className="admin-label" style={{ marginTop: "16px" }}>
              Helpful Tip / Fun Fact (Optional)
            </label>
            <p style={{ 
              fontSize: "0.85em", 
              color: "var(--color-text-secondary)", 
              marginTop: "-4px", 
              marginBottom: "8px" 
            }}>
              * This only shows when the clue is answered
            </p>
            <textarea
              className="admin-input admin-textarea"
              rows="3"
              value={clue.helpfultip}
              onChange={(e) => setClue({ ...clue, helpfultip: e.target.value })}
              placeholder="Optional helpful advice or fun fact for players..."
            />
          </div>

          {/* NEXT LOCATION */}
          <div className="section-box">
            <h2 className="section-title">📍 Next Location</h2>

            {missingNextLocation && (
              <p style={{ 
                color: "var(--color-warning)", 
                marginBottom: "12px",
                padding: "10px 16px",
                background: "rgba(245, 158, 11, 0.1)",
                borderRadius: "8px",
                border: "1px solid rgba(245, 158, 11, 0.2)"
              }}>
                ⚠️ This clue does not yet have a next location. Add one below.
              </p>
            )}

            <label className="admin-label">
              Coordinates (Optional)
              <span style={{ fontSize: "0.9em", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                Format: 53.2743872, -9.0518509
              </span>
            </label>
            <input
              className="admin-input"
              placeholder="53.2743872, -9.0518509"
              value={clue.nextlocation}
              onChange={(e) => setClue({ ...clue, nextlocation: e.target.value })}
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            />

            {mapEmbed && (
              <div style={{ marginTop: "16px" }}>
                <p style={{ color: "var(--color-success)", fontSize: "0.9em", marginBottom: "12px" }}>
                  ✓ Valid coordinates
                </p>
                <iframe
                  src={mapEmbed}
                  allowFullScreen
                  loading="lazy"
                  title="Map Preview"
                  style={{
                    width: "100%",
                    height: "320px",
                    border: "0",
                    borderRadius: "12px",
                    boxShadow: "var(--shadow-md)"
                  }}
                />
              </div>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ 
            display: "flex", 
            gap: "12px", 
            marginTop: "30px",
            paddingTop: "20px",
            borderTop: "2px solid var(--color-border)"
          }}>
            <button 
              type="submit"
              className="dash-btn primary" 
              disabled={saving}
              style={{ flex: 1 }}
            >
              {saving ? "Saving…" : "💾 Save Changes"}
            </button>

            <button
              type="button"
              className="dash-btn secondary"
              onClick={() => navigate(`/trailseditor/${trailId}/clues`)}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}